/* eslint-disable @typescript-eslint/no-deprecated */

import { useFormikContext, useField } from 'formik'
import * as React from 'react'
import styled from 'styled-components'
import { FontWeight, SmallBody } from '../../buildingBlocks/Typography/Text'
import { Input, Text } from '../../styles'
import { Required } from './Required'
import { WarningIcon } from './WarningIcon'

type InputProps = Omit<React.HTMLProps<HTMLInputElement>, 'value' | 'onChange' | 'as' | 'ref'>

export interface FieldProps extends InputProps {
  name: string
  label: string
  helperText?: string
  showValidationErrorMessages?: boolean
  required?: boolean
  warningIconRightIndentRem?: 1.5 | 4.5
  /*
   * autoComplete adds the autocomplete attribute detailed in the link below to the HTML input
   * the list of allowed options is not exhaustive, for that see the link
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
   */
  autoComplete?: 'email' | 'one-time-code' | 'current-password' | 'new-password' | 'given-name' | 'family-name'
}

export const Field: React.FC<FieldProps> = ({
  name,
  label,
  helperText,
  showValidationErrorMessages,
  required = false,
  autoComplete,
  warningIconRightIndentRem,
  ...rest
}) => {
  const { setFieldValue, getFieldMeta, setFieldTouched, submitCount, submitForm } = useFormikContext()
  const [field] = useField(name)

  const { touched, error } = getFieldMeta(name)

  const hasError = touched && error != null && submitCount > 0

  return (
    <Container>
      <Label as="label" htmlFor={name}>
        {label}
        {required && <Required />}
      </Label>
      <StyledInput
        id={name}
        autoComplete={autoComplete}
        onChange={(e) => setFieldValue(field.name, e.target.value)}
        onFocus={() => setFieldTouched(name, true)}
        value={field.value}
        {...rest}
        hasError={hasError}
        onKeyDown={(e) => (e.keyCode === 13 ? submitForm() : {})}
      />
      {(showValidationErrorMessages && hasError) || helperText != null ? (
        <HelperText isError={(hasError && showValidationErrorMessages) ?? false}>
          {hasError && error ? error : helperText}
        </HelperText>
      ) : null}
      {hasError && <WarningIcon indent={warningIconRightIndentRem} />}
    </Container>
  )
}

const Container = styled.div`
  position: relative;
  margin: 1rem 0;
`
interface StyledInputProps {
  hasError: boolean
}

const StyledInput = styled(Input)<StyledInputProps>`
  box-sizing: border-box;
  width: 100%;
  border-radius: 0.8rem;
  border: 1px solid ${({ hasError, theme }) => (hasError ? theme.colors.danger.dark : theme.colors.grey.light)};
  padding: 1.5rem 1rem;
  margin: 1.2rem 0 0 0;
  color: ${({ theme }) => theme.colors.primary.medium};
  &:active,
  &:focus {
    border: 1px solid ${({ theme, hasError }) => (hasError ? theme.colors.danger.dark : theme.colors.grey.light)};
    outline: none;
  }
  &::placeholder {
    color: ${({ theme }) => theme.colors.grey.light};
  }
`

const Label = styled(SmallBody)`
  margin: 0;
  line-height: 2.4rem;
  font-weight: ${FontWeight.semiBold};
  position: absolute;
  top: 0;
  left: 2.9rem;
  background: linear-gradient(${({ theme }) => theme.colors.white} 70%, transparent 70%);
  pointer-events: none;
  padding: 0 0.2rem;
`

export const HelperText = styled(Text)<{ isError: boolean }>`
  font-size: 1.3rem;
  position: absolute;
  color: ${(p) => (p.isError ? p.theme.colors.danger.dark : p.theme.colors.dark)};
  left: 2.9rem;
  bottom: -0.4rem;
  line-height: 1.3rem;
  background-color: white;
`
